import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  InputAdornment,
  Paper,
  Fade,
  CardMedia,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Tag as TagIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  BookmarkBorder as BookmarkIcon,
  Share as ShareIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  Twitter as TwitterIcon,
  Telegram as TelegramIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  GitHub as GitHubIcon,
  LinkedIn as LinkedInIcon,
} from '@mui/icons-material';
import articleApi from '../services/articleApi';
import { toast } from 'react-toastify';

const Home = () => {
  const [articles, setArticles] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');

  // 計算當前年月
  const getCurrentYearMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    let month = String(now.getMonth() + 1).padStart(2, '0');
    
    // 如果月份在5-11月之間，設為12月
    if (parseInt(month) >= 5 && parseInt(month) <= 11) {
      month = '12';
    }
    
    return `${year}-${month}`;
  };

  // 社群連結配置
  const socialLinks = [
    {
      name: 'Facebook',
      icon: <FacebookIcon />,
      url: 'https://www.facebook.com/CJ.Hsu.Instructor/',
      color: '#1877F2'
    },
    {
      name: 'Instagram',
      icon: <InstagramIcon />,
      url: 'https://www.instagram.com/cj_snowboard/',
      color: '#E4405F'
    },
    /*{
      name: 'YouTube',
      icon: <YouTubeIcon />,
      url: 'https://youtube.com/yourchannel',
      color: '#FF0000'
    },*/
    /*{
      name: 'Twitter',
      icon: <TwitterIcon />,
      url: 'https://twitter.com/yourpage',
      color: '#1DA1F2'
    },*/
    /*{
      name: 'Telegram',
      icon: <TelegramIcon />,
      url: 'https://t.me/yourchannel',
      color: '#0088CC'
    },*/
    /*{
      name: 'WhatsApp',
      icon: <WhatsAppIcon />,
      url: 'https://wa.me/yournumber',
      color: '#25D366'
    },*/
    {
      name: 'Email',
      icon: <EmailIcon />,
      url: 'mailto:itwchsu@gmail.com',
      color: '#EA4335'
    },
    /*{
      name: 'GitHub',
      icon: <GitHubIcon />,
      url: 'https://github.com/yourusername',
      color: '#333333'
    },*/
    /*{
      name: 'LinkedIn',
      icon: <LinkedInIcon />,
      url: 'https://linkedin.com/in/yourprofile',
      color: '#0077B5'
    }*/
  ];

  // 載入文章和標籤
  useEffect(() => {
    loadArticles();
    loadTags();
  }, [searchTerm, selectedTag]);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (selectedTag) params.tag = selectedTag;
      
      const data = await articleApi.getArticles(params);
      setArticles(data.results || data);
    } catch (error) {
      toast.error('載入文章失敗');
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      const data = await articleApi.getTags();
      setTags(data.tags || []);
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRelativeTime = (dateString) => {
    const now = new Date();
    const articleDate = new Date(dateString);
    const diffInHours = Math.floor((now - articleDate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return '剛剛發布';
    if (diffInHours < 24) return `${diffInHours} 小時前`;
    if (diffInHours < 48) return '昨天';
    return formatDate(dateString);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      py: 4
    }}>
      <Container maxWidth="lg">
        {/* 英雄區域 */}
        <Fade in timeout={1000}>
          <Paper 
            elevation={10}
            sx={{ 
              p: 6, 
              mb: 6, 
              textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 3,
              color: 'white'
            }}
          >
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                mb: 2,
                fontSize: {
                  xs: '2rem',    // 手機 (0px+)
                  sm: '2.5rem',  // 平板 (600px+)
                  md: '3rem',    // 桌面 (900px+)
                  lg: '3.5rem',  // 大桌面 (1200px+)
                  xl: '4rem'     // 超大桌面 (1536px+)
                }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
              滑雪裝忙協會
              </Box>
              <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
              🏂 ASA
              </Box>
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                opacity: 0.9,
                textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                maxWidth: '600px',
                mx: 'auto',
                mb: 4,
                fontSize: {
                  xs: '1rem',    // 手機
                  sm: '1.2rem',  // 平板
                  md: '1.4rem',  // 桌面
                  lg: '1.6rem',  // 大桌面
                  xl: '1.8rem'   // 超大桌面
                }
              }}
            >
              分享我的滑雪經驗、技巧和心得
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Button
                href={`/public/coach/Ng==/schedule/${getCurrentYearMonth()}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: 'rgba(123,73,192,0.43)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  backdropFilter: 'blur(5px)',
                  px: {
                    xs: 3,    // 手機
                    sm: 4,    // 平板
                    md: 5,    // 桌面
                    lg: 6     // 大桌面
                  },
                  py: {
                    xs: 1,    // 手機
                    sm: 1.5,  // 平板
                    md: 2,    // 桌面
                    lg: 2.5   // 大桌面
                  },
                  fontSize: {
                    xs: '0.9rem',   // 手機
                    sm: '1rem',     // 平板
                    md: '1.1rem',   // 桌面
                    lg: '1.2rem'    // 大桌面
                  },
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
                  }
                }}
              >
                我的雪季行程表 🗓️
              </Button>
              
              <Button
                href="/query-evaluation"
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: 'rgba(76,175,80,0.43)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  backdropFilter: 'blur(5px)',
                  px: {
                    xs: 3,    // 手機
                    sm: 4,    // 平板
                    md: 5,    // 桌面
                    lg: 6     // 大桌面
                  },
                  py: {
                    xs: 1,    // 手機
                    sm: 1.5,  // 平板
                    md: 2,    // 桌面
                    lg: 2.5   // 大桌面
                  },
                  fontSize: {
                    xs: '0.9rem',   // 手機
                    sm: '1rem',     // 平板
                    md: '1.1rem',   // 桌面
                    lg: '1.2rem'    // 大桌面
                  },
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(76,175,80,0.6)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(76,175,80,0.3)'
                  }
                }}
              >
                上課紀錄查詢 📊
              </Button>
            </Box>

            {/* 社群連結 */}
            {/*
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              {socialLinks.map((social) => (
                <Tooltip key={social.name} title={social.name}>
                  <IconButton
                    component={Link}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: social.color,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 20px ${social.color}40`,
                      }
                    }}
                  >
                    {social.icon}
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
            */}
          </Paper>
        </Fade>

        {/* 搜尋和標籤區域 */}
        <Fade in timeout={1200}>
          <Paper 
            elevation={8}
            sx={{ 
              p: {
                xs: 2,    // 手機
                sm: 3,    // 平板
                md: 4,    // 桌面
                lg: 5     // 大桌面
              }, 
              mb: 4,
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: '1px solid rgba(255,255,255,0.3)'
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                placeholder="搜尋文章標題或內容..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  minWidth: {
                    xs: 250,  // 手機
                    sm: 300,  // 平板
                    md: 350,  // 桌面
                    lg: 400   // 大桌面
                  },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    fontSize: {
                      xs: '0.9rem',  // 手機
                      sm: '1rem',    // 平板
                      md: '1.1rem'   // 桌面
                    }
                  }
                }}
              />
              <Chip
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TagIcon fontSize="small" /> 全部標籤
                  </Box>
                }
                onClick={() => setSelectedTag('')}
                color={selectedTag ? 'default' : 'primary'}
                variant={selectedTag === selectedTag ? 'outlined' : 'filled'}
                sx={{ 
                  borderRadius: 2,
                  fontSize: {
                    xs: '0.8rem',  // 手機
                    sm: '0.9rem',  // 平板
                    md: '1rem'     // 桌面
                  }
                }}
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onClick={() => setSelectedTag(selectedTag === tag ? '' : tag)}
                  color={selectedTag === tag ? 'primary' : 'default'}
                  variant={selectedTag === tag ? 'filled' : 'outlined'}
                  sx={{ 
                    borderRadius: 2,
                    fontSize: {
                      xs: '0.75rem',  // 手機
                      sm: '0.85rem',  // 平板
                      md: '0.95rem'   // 桌面
                    },
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 2
                    }
                  }}
                />
              ))}
            </Box>
          </Paper>
        </Fade>

        {/* 文章列表 */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress size={60} thickness={4} />
          </Box>
        ) : articles.length === 0 ? (
          <Fade in timeout={1000}>
            <Alert 
              severity="info" 
              sx={{ 
                textAlign: 'center',
                borderRadius: 3,
                py: 4,
                background: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(10px)'
              }}
            >
              {searchTerm || selectedTag ? '沒有找到符合條件的文章' : '還沒有任何文章，快來分享第一篇吧！'}
            </Alert>
          </Fade>
        ) : (
          <Grid container spacing={4}>
            {articles.map((article, index) => (
              <Grid item xs={12} md={6} lg={4} key={article.id}>
                <Fade in timeout={1000 + index * 100}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: 3,
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    {/* 文章圖片 */}
                    {/*
                    {article.image && (
                      <CardMedia
                        component="img"
                        height="200"
                        image={article.image}
                        alt={article.title}
                        sx={{
                          objectFit: 'contain',  // 改為 contain 以顯示整張圖片
                          backgroundColor: '#f5f5f5',  // 添加背景色
                          transition: 'transform 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.05)'
                          }
                        }}
                      />
                    )}
                    */}

                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      {/* 標題 */}
                      <Typography 
                        variant="h6" 
                        component="h2" 
                        gutterBottom
                        sx={{ 
                          fontWeight: 'bold',
                          lineHeight: 1.3,
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          fontSize: {
                            xs: '1rem',    // 手機
                            sm: '1.1rem',  // 平板
                            md: '1.25rem', // 桌面
                            lg: '1.5rem'   // 大桌面
                          }
                        }}
                      >
                        {article.title}
                      </Typography>

                      {/* 內容預覽 */}
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 3,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.6,
                          fontSize: {
                            xs: '0.8rem',   // 手機
                            sm: '0.9rem',   // 平板
                            md: '1rem'      // 桌面
                          }
                        }}
                      >
                        {article.content.replace(/<[^>]*>/g, '')}
                      </Typography>

                      {/* 標籤 */}
                      <Box sx={{ mb: 3 }}>
                        {article.tag_list.slice(0, 3).map((tag) => (
                          <Chip 
                            key={tag} 
                            label={tag} 
                            size="small" 
                            sx={{ 
                              mr: 1, 
                              mb: 1,
                              borderRadius: 2,
                              backgroundColor: 'rgba(102, 126, 234, 0.1)',
                              color: 'primary.main',
                              fontWeight: 'medium'
                            }} 
                          />
                        ))}
                        {article.tag_list.length > 3 && (
                          <Chip 
                            label={`+${article.tag_list.length - 3}`} 
                            size="small" 
                            sx={{ 
                              mr: 1, 
                              mb: 1,
                              borderRadius: 2,
                              backgroundColor: 'rgba(0,0,0,0.1)',
                              color: 'text.secondary'
                            }} 
                          />
                        )}
                      </Box>

                      <Divider sx={{ mb: 2 }} />

                      {/* 作者和時間信息 */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar 
                          sx={{ 
                            width: 32, 
                            height: 32,
                            backgroundColor: 'primary.main',
                            fontSize: '0.875rem'
                          }}
                        >
                          {article.author.username.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 'medium' }}>
                            {article.author.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getRelativeTime(article.created_at)}
                          </Typography>
                        </Box>
                        <Tooltip title="閱讀次數">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <VisibilityIcon fontSize="small" color="action" />
                            <Typography variant="caption" color="text.secondary">
                              {Math.floor(Math.random() * 1000) + 1}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </Box>
                    </CardContent>

                    <CardActions sx={{ p: 3, pt: 0 }}>
                      <Button 
                        size="medium" 
                        href={`/articles/${article.slug}`}
                        variant="contained"
                        sx={{ 
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 'medium',
                          px: {
                            xs: 2,    // 手機
                            sm: 2.5,  // 平板
                            md: 3,    // 桌面
                            lg: 3.5   // 大桌面
                          },
                          py: {
                            xs: 0.5,  // 手機
                            sm: 0.75, // 平板
                            md: 1,    // 桌面
                            lg: 1.25  // 大桌面
                          },
                          fontSize: {
                            xs: '0.8rem',   // 手機
                            sm: '0.9rem',   // 平板
                            md: '1rem',     // 桌面
                            lg: '1.1rem'    // 大桌面
                          }
                        }}
                      >
                        閱讀全文
                      </Button>
                      <Box sx={{ flex: 1 }} />
                      {/*<Tooltip title="收藏">
                        <IconButton size="small" color="primary">
                          <BookmarkIcon />
                        </IconButton>
                      </Tooltip>*/}
                      <Tooltip title="分享">
                        <IconButton size="small" color="primary"
                          onClick={async () => {
                            try {
                              if (navigator.share) {
                                await navigator.share({
                                  title: article.title,
                                  //text: article.content,
                                  url: window.location.href + `articles/${article.slug}`,
                                });
                              } else {
                                await navigator.clipboard.writeText(window.location.href + `articles/${article.slug}`);
                                toast.success('連結已複製到剪貼簿');
                              }
                            } catch (error) {
                              // 處理分享被取消或其他錯誤
                              if (error.name === 'AbortError') {
                                // 用戶取消了分享，不需要顯示錯誤
                                console.log('分享被取消');
                              } else {
                                // 其他錯誤，嘗試複製到剪貼板
                                try {
                                  await navigator.clipboard.writeText(window.location.href + `articles/${article.slug}`);
                                  toast.success('連結已複製到剪貼簿');
                                } catch (clipboardError) {
                                  console.error('複製失敗:', clipboardError);
                                  toast.error('分享失敗');
                                }
                              }
                            }
                          }}
                        >
                          <ShareIcon />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        )}

        {/* 頁腳社群連結 */}
        <Fade in timeout={1500}>
          <Paper 
            elevation={4}
            sx={{ 
              mt: 8, 
              p: 4,
              textAlign: 'center',
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: '1px solid rgba(255,255,255,0.2)'
            }}
          >
            <Typography variant="h6" sx={{ 
              color: 'white', 
              mb: 3, 
              fontWeight: 'medium',
              fontSize: {
                xs: '1.1rem',  // 手機
                sm: '1.3rem',  // 平板
                md: '1.5rem',  // 桌面
                lg: '1.75rem'  // 大桌面
              }
            }}>
              我的社群媒體
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              {socialLinks.map((social) => (
                <Tooltip key={social.name} title={social.name}>
                  <IconButton
                    component={Link}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: social.color,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 20px ${social.color}40`,
                      }
                    }}
                  >
                    {social.icon}
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
            <Typography variant="body2" sx={{ 
              color: 'rgba(255,255,255,0.8)', 
              mt: 2,
              fontSize: {
                xs: '0.8rem',   // 手機
                sm: '0.9rem',   // 平板
                md: '1rem',     // 桌面
                lg: '1.1rem'    // 大桌面
              }
            }}>
              保持聯繫，獲取最新的滑雪資訊和技巧分享
            </Typography>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default Home;